import React, { useState, useEffect, useCallback, useRef } from "react";
import { Search, Download, ChartBar, ChartPie, Clock, Info } from "lucide-react";
import axios from "axios";
import { useSelector } from "react-redux";
import CostFilters from './CostFilters';
import CostExplorerChart from './Graphs/CostExplorerChart';
import AnnualBudgetTracker from './AnnualBudgetTracker';
import AllEpDataGraph from './Graphs/EpDataGraph';
import CostManagementCards from './CostManagementCards';
import BudgetAccountTable from './BudgetAccountTable';
import UnauthorizedPage from "../UnauthorizedPage";
const CostManagement = () => {
  const [budgetAccountData, setBudgetAccountData] = useState([]);
  const [loading, setLoading] = useState(true);
  const abortControllerRef = useRef(null);
  const [epNoOemData, setEpNoOemData] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [costData, setCostData] = useState({
    totalCost: 0,
    serviceCosts: [],
    regionCosts: [],
    dimensionGraphData: []
  });
  const [accountMappings, setAccountMappings] = useState({});
  const [staticCostData, setStaticCostData] = useState({
    currentMonthCost: 0,
    forecastedCost: 0,
    previousMonthTotalCost: 0,
    topAccountName: "",
    topEpNo: "",
    topRegion: "",
    topService: "",
    topServiceCost: 0,
    topRegionCost: 0,
    topEpNoCost: 0,
    topAccountCost: 0,
    totalIncreasePercentage: 0,
    yearToDateCost: 0,
    monthlyTotalCosts: []
  });
  const [budgetData, setBudgetData] = useState({
    id: "",
    Year: new Date().getFullYear(),
    Budget: 0
  });
  const [accountDetails, setAccountDetails] = useState([]);
  const [filters, setFilters] = useState({
    accountName: [],
    regions: [],
    services: [],
    apiOperations: [],
    groupBy: "account",
    granularity: "monthly",
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });
  const [availableFilters, setAvailableFilters] = useState({
    regions: [],
    services: [],
    apiOperations: []
  });
  const [error, setError] = useState(null);
  const [graphData, setGraphData] = useState([]);
  const accessToken = useSelector((state) => state.accessToken.value);
  const apiUrl = process.env.REACT_APP_API_URL;

  const query = `
    query GetCostData($filters: CostFiltersInput!) {
      getMainCostPageDetails(filters: $filters) {
        dimensionGraphData {
          groupByDimension
          monthOrDate
          totalCost
          year
        }
        regionCosts {
          costCategory
          totalCost
        }
        apiOperationCosts {
          costCategory
          totalCost
        }
        serviceCosts {
          costCategory
          totalCost
        }
        totalCost
      }
    }
  `;

  const accountsQuery = `
    query MyQuery {
      getAWSAccounts {
        AccountName
        AccountNo
        id
      }
    }
  `;

  const staticDataQuery = `
    query MyQuery {
      getCostPageStaticData {
        currentMonthCost
        forecastedCost
        previousMonthTotalCost
        topAccountName
        topEpNo
        topRegion
        topService
        totalIncreasePercentage
        yearToDateCost
        topServiceCost
        topRegionCost
        topEpNoCost
        topAccountCost
        currentMonthAccountCosts {
           accountId
           totalCost
        }
        monthlyTotalCosts {
          cost
          month
          year
        }
      }
      getBudgetAccountTable {
        AccountName
        AccountNo
        Budget
        Users
        id
      }
    }
  `;

  const budgetQuery = `
    query MyQuery {
      getYearBudget {
        id
        Year
        Budget
      }
    }
  `;

  const epNoOemQuery = `
  query MyQuery {
    getEpNoandOem {
      id
      EpNo
      Oem
    }
  }
`;

  const updateAlertUsersMutation = `
    mutation UpdateBudgetAlertUsers($id: ID!, $users: [String!]!) {
      updateBudgetAlertUsers(id: $id, Users: $users)
    }
  `;

  useEffect(() => {
    if (costData.dimensionGraphData || costData.dimensionGraphData.length > 0) {
      const groupedData = {};

      costData.dimensionGraphData.forEach(item => {
        const dateKey = filters.granularity === 'monthly'
          ? `${item.year}-${String(item.monthOrDate).padStart(2, '0')}`
          : item.monthOrDate;

        if (!groupedData[dateKey]) {
          groupedData[dateKey] = { date: dateKey };
        }

        const dimensionKey = item.groupByDimension;
        const dimensionValue = (filters.groupBy === 'account' && accountMappings[dimensionKey])
          ? accountMappings[dimensionKey]
          : dimensionKey;
        groupedData[dateKey][dimensionValue] = item.totalCost || 0;
      });

      setGraphData(Object.values(groupedData).sort((a, b) => {
        if (a.date < b.date) return -1;
        if (a.date > b.date) return 1;
        return 0;
      }));
    }
  }, [costData]);

  const handleToggleAlert = async (account, userName, userEmail) => {
    try {
      const currentUsers = Array.isArray(account.Users) ? [...account.Users] : [];
      const isReceivingAlerts = currentUsers.includes(userName) || currentUsers.includes(userEmail);
      let updatedUsers;

      if (isReceivingAlerts) {
        updatedUsers = currentUsers.filter(user =>
          user !== userName && user !== userEmail
        );
      } else {
        updatedUsers = [...currentUsers, userEmail];
      }

      const response = await axios.post(
        apiUrl,
        {
          query: updateAlertUsersMutation,
          variables: {
            id: account.id,
            users: updatedUsers
          }
        },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      if (response.data.data) {
        setBudgetAccountData(prev => prev.map(item => {
          if (item.id === account.id) {
            return {
              ...item,
              Users: updatedUsers
            };
          }
          return item;
        }));
      }
    } catch (err) {
      console.error("Error updating alert subscription:", err);
      setError("Failed to update alert subscription");
    }
  };

  const updateAvailableFilters = (data) => {
    if (!data) return;

    const newRegions = data.regionCosts?.map(item => item.costCategory) || [];
    const uniqueNewRegions = [...new Set(newRegions)];

    const newServices = data.serviceCosts?.map(item => item.costCategory) || [];
    const uniqueNewServices = [...new Set(newServices)];

    const newApiOperations = data.apiOperationCosts?.map(item => item.costCategory) || [];
    const uniqueNewApiOperations = [...new Set(newApiOperations)];

    setAvailableFilters(prevFilters => ({
      regions: [...new Set([...(prevFilters.regions || []), ...uniqueNewRegions])],
      services: [...new Set([
        ...(prevFilters.services || []),
        ...uniqueNewServices.map(service => service === 'Total Taxes' ? 'Tax' : service)
      ])],
      apiOperations: [...new Set([...(prevFilters.apiOperations || []), ...uniqueNewApiOperations])]
    }));
  };

  const fetchBudgetData = async () => {
    try {
      const response = await axios.post(
        apiUrl,
        { query: budgetQuery },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      if (response.data.data && response.data.data.getYearBudget) {
        const data = response.data.data.getYearBudget;
        setBudgetData(data);
      }
    } catch (error) {
      console.error("Error fetching budget data:", error);
    }
  };

  const fetchAccountData = async () => {
    try {
      const response = await axios.post(
        apiUrl,
        { query: accountsQuery },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      if (response.data.data && response.data.data.getAWSAccounts) {
        const accounts = response.data.data.getAWSAccounts;
        const mappings = {};
        accounts.forEach(account => {
          if (mappings[account.AccountNo]) {
            mappings[account.AccountNo] = `${mappings[account.AccountNo]}, ${account.AccountName}`;
          } else {
            mappings[account.AccountNo] = account.AccountName;
          }
        });
        setAccountMappings(mappings);
      }
    } catch (error) {
      console.error("Error fetching account data:", error);
    }
  };
  const fetchStaticCostData = async () => {
    try {
      const response = await axios.post(
        apiUrl,
        { query: staticDataQuery },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      if (
        response.data.errors &&
        response.data.errors[0].errorType == "Unauthorized"
      ) {
        setAuthError("Unauthorized");
      }
      if (response.data.data && response.data.data.getCostPageStaticData) {
        const data = response.data.data.getCostPageStaticData;
        setStaticCostData({
          currentMonthCost: data.currentMonthCost || 0,
          forecastedCost: data.forecastedCost || 0,
          previousMonthTotalCost: data.previousMonthTotalCost || 0,
          topAccountName: data.topAccountName || "",
          topEpNo: data.topEpNo || "",
          topRegion: data.topRegion || "",
          topService: data.topService || "",
          topAccountCost: data.topAccountCost || 0,
          topEpNoCost: data.topEpNoCost || 0,
          topRegionCost: data.topRegionCost || 0,
          topServiceCost: data.topServiceCost || 0,
          totalIncreasePercentage: data.totalIncreasePercentage || 0,
          yearToDateCost: data.yearToDateCost || 0,
          monthlyTotalCosts: data.monthlyTotalCosts || []
        });

        if (response.data.data.getBudgetAccountTable) {
          const accounts = response.data.data.getBudgetAccountTable || [];
          const accountCosts = data.currentMonthAccountCosts || [];

          const mergedData = accounts.map(account => {
            const costEntry = accountCosts.find(cost => cost.accountId === account.AccountNo);
            const currentCost = costEntry ? costEntry.totalCost : 0;
            const budget = account.Budget || 0;

            const percentage = budget > 0 ? (currentCost / budget * 100) : 0;
            const beyondBudget = currentCost > budget ? currentCost - budget : 0;

            return {
              ...account,
              currentCost,
              percentage,
              beyondBudget
            };
          });

          setBudgetAccountData(mergedData);
        }
      }
    } catch (error) {
      console.error("Error fetching static cost data:", error);
    }
  };
  const fetchEpNoOemData = async () => {
    try {
      const response = await axios.post(
        apiUrl,
        { query: epNoOemQuery },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      if (response.data.data && response.data.data.getEpNoandOem) {
        setEpNoOemData(response.data.data.getEpNoandOem);
      }
    } catch (error) {
      console.error("Error fetching EP No and OEM data:", error);
    }
  };

  const fetchCostData = async () => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      if (graphData.length > 0) {
        setIsUpdating(true);
      } else {
        setLoading(true);
      }
      const variables = {
        filters: {
          accountName: filters.accountName,
          apiOperations: filters.apiOperations,
          regions: filters.regions,
          services: filters.services,
          groupBy: filters.groupBy,
          granularity: filters.granularity,
          startDate: filters.startDate,
          endDate: filters.endDate
        }
      };

      const response = await axios.post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
          signal: controller.signal // Add abort signal
        }
      );


      if (abortControllerRef.current !== controller) {
        return;
      }

      if (response.data.data && response.data.data.getMainCostPageDetails) {
        const data = response.data.data.getMainCostPageDetails;
        setCostData({
          totalCost: data.totalCost || 0,
          serviceCosts: data.serviceCosts || [],
          regionCosts: data.regionCosts || [],
          dimensionGraphData: data.dimensionGraphData || []
        });
        updateAvailableFilters(data);
      }

      if (response.data.errors &&
        response.data.errors[0].errorType === "Unauthorized") {
        setError("Unauthorized");
      }

      // Only update loading state if this is still the current request
      setLoading(false);
      setIsUpdating(false);
    } catch (error) {
      // Don't show error or update loading state if request was aborted
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        console.log('Request was cancelled');
        return;
      }

      // Check if this request is still the current one before updating error state
      if (abortControllerRef.current === controller) {
        console.error("Error fetching cost data:", error);
        setError("Failed to fetch cost data");
        setLoading(false);
        setIsUpdating(false);
      }
    }
  };

  useEffect(() => {
    fetchCostData();
  }, [filters]);

  const filteredBudgetAccountData = React.useMemo(() => {
    if (!accountDetails || accountDetails.length === 0) return [];
    return budgetAccountData.filter(item =>
      accountDetails.some(acc => acc.AccountNo === item.AccountNo)
    );
  }, [budgetAccountData, accountDetails]);

  useEffect(() => {
    fetchStaticCostData();
    fetchBudgetData();
    fetchAccountData();
    fetchEpNoOemData();
  }, []);
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === "dateRange") {
      setFilters(prev => ({
        ...prev,
        startDate: value.startDate,
        endDate: value.endDate
      }));
      return;
    }
    const updatedFilters = { ...filters, [name]: value };
    setFilters(updatedFilters);
  }, [filters, setFilters]);
  if (authError == "Unauthorized") {
    return <UnauthorizedPage />;
  } else {
    return (
      <div className="p-6 space-y-6 min-h-screen bg-gray-50">
        <AnnualBudgetTracker
          budgetData={budgetData}
          staticCostData={staticCostData}
        />

        <CostManagementCards
          staticCostData={staticCostData}
          budgetAccountData={budgetAccountData}
          epNoOemData={epNoOemData}
        />

        <div className="flex flex-col lg:flex-row gap-6">
          <div className="w-full">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 mb-6">
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="w-full lg:w-3/4">
                  <div className="mb-8">
                    {graphData ? (
                      <CostExplorerChart
                        data={graphData}
                        granularity={filters.granularity}
                        groupBy={filters.groupBy}
                        filters={filters}
                        handleFilterChange={handleFilterChange}
                        loading={loading}
                        isUpdating={isUpdating}
                      />
                    ) : (
                      <div className="h-80 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
                        <div className="text-center">
                          <p className="text-gray-500">No data available for the selected filters</p>
                          <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or date range</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="w-full lg:w-1/4">
                  <CostFilters
                    filters={filters}
                    handleFilterChange={handleFilterChange}
                    setAccountDetails={setAccountDetails}
                    accountDetails={accountDetails}
                    availableFilters={availableFilters}
                    setError={setError}
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 mb-6">
              <div className="flex justify-between items-center mb-6">
                <AllEpDataGraph dateRange="1month" />
              </div>
            </div>

            <div className="mb-6">
              <BudgetAccountTable
                budgetAccountData={filteredBudgetAccountData}
                handleToggleAlert={handleToggleAlert}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default CostManagement;